<template lang="pug">
	.contour-editor.flex-col.space(@mousemove="onMouseMove", @mouseup="releaseMoving")
		.weights-words-holder
			.flex-row.full-width.weight-words
				.weight-word(v-for="item in wordsWithWeights", :style="{ flex: item.weight }") {{item.word}}
		.chart.flex-1(ref="chartHolder")
			Loader.loader.center.align-center(v-if="!chartIsShowing")
			DxChart(
				v-if="chartIsShowing"
				id="chart"
				ref="chart"
				:data-source="points"
				:palette="['#f27649']"
				:animation="{ enabled: false }"
				:stickyHovering="false"
				@seriesClick="onSeriesClick"
				@pointHoverChanged="onPointHoverChanged"
			)
				DxCommonSeriesSettings(type="spline", argument-field="x", :hoverStyle="{ width: 2 }")
				DxCommonAxisSettings
					DxGrid(:visible="true")
				DxSeries(key-field="id" value-field="y")
				DxArgumentAxis(:allow-decimals="false" :axis-division-factor="60", :max="100", :min="0", :valueMarginsEnabled="false")
					DxLabel
						DxFormat(type="decimal")
				DxValueAxis(:max="100", :min="-100", :valueMarginsEnabled="false", :visualRange="[-100,100]")
				DxLegend(:visible="false")
				DxExport(:enabled="false")
				DxTooltip(:enabled="false")
				DxSize(:height="300")
		.flex-row.space
			span Original
			.link-button.scale(@click="$emit('play-original')")
				i.fas.fa-volume-up
</template>

<script>
	import _ from 'lodash';
	import $ from 'jquery';
	import DxButton from 'devextreme-vue/button';
	import Loader from 'app-core/Loader.vue';

	import {
		DxChart,
		DxSeries,
		DxArgumentAxis,
		DxValueAxis,
		DxCommonSeriesSettings,
		DxCommonAxisSettings,
		DxGrid,
		DxExport,
		DxLegend,
		DxMargin,
		DxTooltip,
		DxLabel,
		DxFormat,
		DxSize
	} from 'devextreme-vue/chart';

	export default {
		translationCategory: 'insertion.SSMLContourEditor',
		name: 'contour-editor',
		components: {
			DxButton,
			DxChart,
			DxSeries,
			DxArgumentAxis,
			DxValueAxis,
			DxCommonSeriesSettings,
			DxCommonAxisSettings,
			DxGrid,
			DxExport,
			DxLegend,
			DxMargin,
			DxTooltip,
			DxLabel,
			DxFormat,
			DxSize,
			Loader
		},
		model: {
			prop: 'value',
			event: 'change '
		},
		props: ['text', 'value'],
		data() {
			return {
				innerValue: this.value,
				chartIsShowing: false,
				hoveredPoint: null,
				points: this.parsePointsFromValue(this.value)
			};
		},
		computed: {
			wordsWithWeights() {
				let text = (this.text || '').replace(/[,.<>;?!#^*()|/\\/'`^:"]+/g, '');
				return text.split(' ').filter(f => `${f}`.trim()).map(word => {
					let split = word.split('');
					let digitsLength = split.filter(f => [0,1,2,3,4,5,6,7,8,9,0].indexOf(parseInt(f)) > -1);
					let weight = digitsLength === 0 ? 4 : digitsLength === split.length ? 0 : 2;
					if (digitsLength > 0)
						weight += digitsLength * 2;
					return {
						word,
						weight
					};
				});
			}
		},
		watch: {
			points: {
				handler() {
					this.calcValue();
				},
				deep: true
			}
		},
		mounted() {
			if (__DEBUG__)
				window.contourEditor = this;
			this.showChart();
		},
		methods: {
			releaseMoving() {
				this.hoveredPoint = null;
			},
			clearPoints() {
				this.points = this.parsePointsFromValue();
				this.showChart(10);
			},
			calcValue() {
				let filteredPoints = this.points.filter(f => (f.x > 0 && f.x < 100) || f.y !== 0);
				this.innerValue = _.orderBy(filteredPoints, ['x', 'y'])
					.map(f => `(${parseFloat(f.x).toFixed(0)}%, ${f.y >= 0 ? '+' : '-'}${Math.abs(parseFloat(f.y).toFixed(0))}%)`).join(' ');
				if (this.innerValue === '(0%, 0%) (100%, 0%)') {
					this.$emit('change', '');
					return;
				}
				this.$emit('change', this.innerValue);
				if (__DEBUG__)
					console.debug('Contour value changed', this.innerValue);
			},
			parsePointsFromValue(val) {
				let points = [];
				if (val)
					try {
						let values = val.split(') (');
						values.map(v => {
							let value = v.replace(/[()%]+/g, '');
							let split = value.split(',');
							if (__DEBUG__)
								console.debug('Parse values', { v, val });
							let x = parseInt(split[0]);
							let y = parseInt(split[1]);
							points.push({
								id: x === 0 ? 'start' : x === 100 ? 'end' : this.$Data.uuid(),
								x,
								y
							});
						});
					} catch(ex) {
						if (__DEBUG__)
							console.debug('Parsing input values error', { ex });
					}

				if (!points.some(f => f.x === 0))
					points.push({
						id: 'start',
						x: 0,
						y: 0
					});

				if (!points.some(f => f.x === 100))
					points.push({
						id: 'end',
						x: 100,
						y: 0
					})
				return points;
			},
			showChart(timer) {
				this.chartIsShowing = false;
				setTimeout(() => {
					if (this._isDestroyed) return;
					this.chartIsShowing = true;
				}, timer || 500);
			},
			renderChart() {
				this.$refs.chart.instance.refresh();
			},
			onSeriesClick(e) {
				if (__DEBUG__)
					console.debug('On series click', e);
				this.hoveredPoint = null;
				let width = $(this.$refs.chartHolder).width();
				let x = parseInt(e.event.offsetX * 100 / width);

				this.points.push({
					id: this.$Data.uuid(),
					x,
					y: 0
				});

				this.showChart(1);
			},
			onPointHoverChanged() {
				this.hoveredPoint = this.getHoveredPoint();
			},
			getHoveredPoint() {
				let point = null;
				this.$refs.chart.instance.series.forEach((serie) => {
					if (!point) {
						const selectedPoints = serie.getPoints().filter(p => p.isHovered());
						if (selectedPoints.length)
							point = selectedPoints[0];
					}
				});
				return point;
			},
			onMouseMove(e) {
				if (e.buttons === 1 && this.hoveredPoint) {
					let deltaX = e.movementX;
					let deltaY = e.movementY;
					let width = $(this.$refs.chartHolder).width();
					let height = $(this.$refs.chartHolder).height();

					let point = this.points.filter(f => f.id === this.hoveredPoint.data.id)[0];
					if (point) {
						if (['start', 'end'].indexOf(point.id) === -1)
							point.x = Math.max(0, Math.min(100, point.x + deltaX * 100 / width));
						point.y = Math.max(-100, Math.min(100, point.y - deltaY * 200 / height));
						this.renderChart();
					}
				}
			}
		}
	};
</script>

<style lang='scss'>
	.contour-editor {
		.weights-words-holder {
			padding-left: 35px;
			padding-right: 10px;
		}

		.weight-words {
			border: 1px solid rgba(0,0,0,0.05);
			border-radius: 4px;

			.weight-word {
				text-align: center;
				background: rgba(0, 0, 0, 0.03);

				&:not(:last-child) {
					border-right: 1px solid rgba(0,0,0,0.05);
				}
			}
		}

		.chart {
			width: 100%;

			> .loader {
				height: 300px;
			}
		}
	}
</style>
